'use client';

// SignUpPrompt - Modal dialog to encourage sign-up after high engagement

import { useRouter } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import type { PromptType } from './useEngagement';
import { getSessionId } from '../../lib/session';

export interface SignUpPromptProps {
  /** Which type of engagement triggered the prompt */
  promptType: PromptType;
  /** Callback when user dismisses the prompt */
  onDismiss: (remindLater?: boolean) => void;
}

/**
 * Centered modal dialog to encourage unauthenticated users to sign up
 * after demonstrating high engagement with the bot creation process
 *
 * @example
 * ```tsx
 * <SignUpPrompt
 *   promptType="uploads"
 *   onDismiss={(remindLater) => dismissPrompt('uploads', remindLater)}
 * />
 * ```
 */
export function SignUpPrompt({ promptType, onDismiss }: SignUpPromptProps) {
  const t = useTranslations('creator.engagement');
  const router = useRouter();
  const locale = useLocale();

  const handleSignUp = () => {
    // Pass session ID as URL param for reliable bot claiming after verification
    const sessionId = getSessionId();
    const params = new URLSearchParams({
      ...(sessionId && { sid: sessionId }),
    });
    // Redirect back to current page after signup instead of /deploy
    params.set('redirectTo', window.location.pathname);
    // Use quick-signup (magic link + OTP) for minimal friction
    router.push(`/${locale}/quick-signup?${params.toString()}`);
  };

  return (
    <Dialog
      open
      onClose={() => onDismiss(false)}
      maxWidth="xs"
      fullWidth
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(4px)',
          },
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 1,
          background: 'linear-gradient(145deg, rgba(30,30,46,1) 0%, rgba(20,20,35,1) 100%)',
          border: '1px solid rgba(255,255,255,0.08)',
        },
      }}
    >
      <DialogContent sx={{ textAlign: 'center', py: 4, px: 3, position: 'relative' }}>
        <IconButton
          size="small"
          onClick={() => onDismiss(false)}
          aria-label={t('dismiss')}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: 'grey.500',
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        <Typography
          variant="h5"
          sx={{ fontWeight: 700, mb: 1.5, color: 'common.white' }}
        >
          {t(`${promptType}Message`)}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 3, alignItems: 'center' }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleSignUp}
            sx={{
              px: 5,
              py: 1.2,
              fontWeight: 700,
              fontSize: '1rem',
              borderRadius: 2,
              textTransform: 'none',
              width: '100%',
              maxWidth: 280,
            }}
          >
            {t('signUpNow')}
          </Button>

          {promptType === 'time' && (
            <Button
              size="small"
              onClick={() => onDismiss(true)}
              sx={{ color: 'grey.400', textTransform: 'none' }}
            >
              {t('remindLater')}
            </Button>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
